import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Idempotent — also registered globally in dayjsSetup.js; registering here makes
// the module self-contained so tests / future contexts don't need the global setup.
dayjs.extend(relativeTime);

export const CS2_SORT_RANK = {
  quarantined: 0,
  login_error: 1,
  pending: 2,
  cooldown: 3,
  ready: 4,
  not_tracked: 5
};

// Mirror of server/utils/cs2.js PENALTY_REASONS. Intentionally duplicated rather
// than fetched: the server already writes the numeric code on the account doc,
// the label is pure presentation, and both sides can evolve independently.
const PENALTY_REASONS = {
  0:  'None',
  1:  'Kicked',
  2:  'Team-killed at round start',
  3:  'Team-damaged at round start',
  4:  'Team-killed too many times',
  5:  'Team-damaged too much',
  6:  'Teamflashed too much',
  7:  'Match abandoned',
  8:  'Failed to reconnect',
  9:  'Rank cooldown',
  10: 'Convicted by Overwatch (griefing)',
  11: 'Convicted by Overwatch (cheating)',
  12: 'Convicted by Overwatch (other)',
  14: 'Competitive ban',
  17: 'Untrusted',
  22: 'Ranked matchmaking cooldown',
  23: 'Matchmaking ban'
};

export function penaltyReasonLabel(code) {
  const n = Number(code);
  if (!Number.isFinite(n) || n === 0) return null;
  if (PENALTY_REASONS[n]) return PENALTY_REASONS[n];
  return `Unknown reason (${n})`;
}

const EPOCH_ZERO_MS = 0;

function isEpochZero(dateish) {
  // null/undefined (fresh subdoc before first write) and ISO epoch (never checked) are both "pending".
  if (!dateish) return true;
  const t = new Date(dateish).getTime();
  return t === EPOCH_ZERO_MS;
}

export function cs2StatusView(account) {
  if (account?.accountType !== 'credentialed') {
    return { kind: 'not_tracked', label: '—', code: '', color: 'gray', tooltip: 'Not tracked', sortRank: CS2_SORT_RANK.not_tracked };
  }

  const s = account.cs2State || {};

  if (s.quarantined) {
    return {
      kind: 'quarantined',
      label: '🚫 Quarantined',
      code: '🚫',
      color: 'red',
      tooltip: s.lastLoginError || 'Quarantined after repeated login failures',
      sortRank: CS2_SORT_RANK.quarantined
    };
  }

  if ((s.consecutiveFailures || 0) >= 3) {
    return {
      kind: 'login_error',
      label: '⚠ Login Error',
      code: '⚠',
      color: 'yellow',
      tooltip: s.lastLoginError || 'Login error',
      sortRank: CS2_SORT_RANK.login_error
    };
  }

  if (isEpochZero(s.lastCheckedAt)) {
    return {
      kind: 'pending',
      label: '⋯ Pending',
      code: '⋯',
      color: 'gray',
      tooltip: 'Not yet checked',
      sortRank: CS2_SORT_RANK.pending
    };
  }

  const expiry = s.cooldownExpiresAt ? new Date(s.cooldownExpiresAt) : null;
  if (s.inCooldown && expiry && expiry.getTime() > Date.now()) {
    const relative = dayjs(expiry).fromNow(true); // "2 hours"
    const reason = penaltyReasonLabel(s.penaltyReason);
    const tooltipBase = `Cooldown expires ${dayjs(expiry).format('YYYY-MM-DD HH:mm')}`;
    return {
      kind: 'cooldown',
      label: `CD: ${relative} (${s.cooldownLevel})`,
      code: 'CD',
      color: 'orange',
      tooltip: reason ? `${tooltipBase}\nReason: ${reason}` : tooltipBase,
      sortRank: CS2_SORT_RANK.cooldown,
      expiresAtMs: expiry.getTime()
    };
  }

  return {
    kind: 'ready',
    label: 'Ready',
    code: '✓',
    color: 'green',
    tooltip: 'Ready to play competitive',
    sortRank: CS2_SORT_RANK.ready
  };
}
